import { MoreHorizontal, Check, Pencil, FlaskConical, RefreshCw, Trash2, Bot } from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui';
import type { Credential } from '@/types/identity';

export type CredentialAction =
  | 'set-default'
  | 'rename'
  | 'verify'
  | 'test-as-agent'
  | 'reconnect'
  | 'revoke';

interface Props {
  credential: Credential;
  /** Caller can perform write actions for this scope (computed by the page). */
  canManage: boolean;
  /** Whether at least one sibling exists in the same (principal, provider) group. */
  hasSiblings: boolean;
  onAction: (action: CredentialAction) => void;
}

/**
 * The `⋯` overflow menu on each account row.
 *
 * Visibility rules:
 *   - `verify` is always shown — even members can test a credential they
 *     can see (the backend allows it on space-service creds without admin).
 *   - The remaining write actions (`set-default`, `rename`, `reconnect`,
 *     `revoke`) only render when `canManage` is true.
 *   - `set-default` is further hidden when the credential is already the
 *     default or it's the only account in its group (nothing to swap to).
 *   - `reconnect` is OAuth-only — API-key credentials get re-stored via
 *     the Add modal, not a reconnect flow.
 *   - `test-as-agent` is shown for `aws_role` credentials to managers: a
 *     simulated AssumeRole as a chosen agent (trust policies may pin
 *     `optr-agent-id`, which the plain test can't satisfy).
 *   - `rename` and `reconnect` are both hidden for `type === 'session'`
 *     credentials (aws-sso device-login + derived role keys): their label is
 *     backend-synthesized, and re-auth goes through the device flow, not here.
 */
export default function CredentialActionsMenu({
  credential,
  canManage,
  hasSiblings,
  onAction,
}: Props) {
  const isDefault = credential.metadata.isDefault === true;
  const isOAuth = credential.authType === 'oauth2';
  const isSession = credential.type === 'session';
  const isAwsRole = credential.authType === 'aws_role';

  const showSetDefault = canManage && !isDefault && hasSiblings;
  const showRename = canManage && !isSession;
  const showReconnect = canManage && isOAuth && !isSession;
  const showRevoke = canManage;
  const showTestAsAgent = canManage && isAwsRole;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          aria-label="Credential actions"
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={() => onAction('verify')}>
          <FlaskConical className="h-4 w-4" />
          Test connection
        </DropdownMenuItem>
        {showTestAsAgent && (
          <DropdownMenuItem onSelect={() => onAction('test-as-agent')}>
            <Bot className="h-4 w-4" />
            Test as agent…
          </DropdownMenuItem>
        )}
        {showSetDefault && (
          <DropdownMenuItem onSelect={() => onAction('set-default')}>
            <Check className="h-4 w-4" />
            Set as default
          </DropdownMenuItem>
        )}
        {showRename && (
          <DropdownMenuItem onSelect={() => onAction('rename')}>
            <Pencil className="h-4 w-4" />
            Rename
          </DropdownMenuItem>
        )}
        {showReconnect && (
          <DropdownMenuItem onSelect={() => onAction('reconnect')}>
            <RefreshCw className="h-4 w-4" />
            Reconnect
          </DropdownMenuItem>
        )}
        {showRevoke && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => onAction('revoke')}
              className="text-red-600 focus:text-red-700"
            >
              <Trash2 className="h-4 w-4" />
              Revoke
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
